export interface CacheEntryMeta {
  key: string;
  updatedAt: string;
}

export interface CacheManifestEntry {
  version: number;
  updatedAt: string;
  entries: Record<string, CacheEntryMeta>;
}

export interface CacheManifest {
  updatedAt: string;
  schemas: Record<string, CacheManifestEntry>;
}

const MANIFEST_KEY = "meethub.cache.manifest";
const CACHE_PREFIX = "meethub.cache";

function getStorage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return window.localStorage;
}

function readManifest(): CacheManifest | null {
  const storage = getStorage();
  if (!storage) return null;
  const raw = storage.getItem(MANIFEST_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CacheManifest;
  } catch (error) {
    console.warn("[VersionedCache] Failed to parse manifest", error);
    return null;
  }
}

function writeManifest(manifest: CacheManifest) {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(MANIFEST_KEY, JSON.stringify(manifest));
}

function clearSchemaEntries(entry: CacheManifestEntry | undefined) {
  const storage = getStorage();
  if (!storage || !entry) return;
  Object.values(entry.entries).forEach(({ key }) => {
    storage.removeItem(key);
  });
}

function buildEntryKey(schema: string, entryKey: string) {
  return `${CACHE_PREFIX}.${schema}.${entryKey}`;
}

function ensureManifest() {
  const existing = readManifest();
  if (existing) return existing;
  const fresh: CacheManifest = { updatedAt: new Date().toISOString(), schemas: {} };
  writeManifest(fresh);
  return fresh;
}

export function ensureCacheVersion(schema: string, version: number): CacheManifestEntry {
  const manifest = ensureManifest();
  const currentEntry = manifest.schemas[schema];
  if (!currentEntry || currentEntry.version !== version) {
    clearSchemaEntries(currentEntry);
    const nextEntry: CacheManifestEntry = {
      version,
      updatedAt: new Date().toISOString(),
      entries: {},
    };
    const nextManifest: CacheManifest = {
      updatedAt: new Date().toISOString(),
      schemas: { ...manifest.schemas, [schema]: nextEntry },
    };
    writeManifest(nextManifest);
    return nextEntry;
  }
  return currentEntry;
}

export function setCacheEntry<T>(schema: string, version: number, key: string, value: T) {
  const storage = getStorage();
  if (!storage) return;

  const schemaEntry = ensureCacheVersion(schema, version);
  const entryKey = buildEntryKey(schema, key);

  const manifest = ensureManifest();
  const updatedManifest: CacheManifest = {
    updatedAt: new Date().toISOString(),
    schemas: {
      ...manifest.schemas,
      [schema]: {
        ...schemaEntry,
        updatedAt: new Date().toISOString(),
        entries: {
          ...schemaEntry.entries,
          [key]: {
            key: entryKey,
            updatedAt: new Date().toISOString(),
          },
        },
      },
    },
  };

  storage.setItem(entryKey, JSON.stringify(value));
  writeManifest(updatedManifest);
}

export function getCacheEntry<T>(schema: string, version: number, key: string): T | null {
  const storage = getStorage();
  if (!storage) return null;

  const schemaEntry = ensureCacheVersion(schema, version);
  const entryKey = buildEntryKey(schema, key);

  if (!schemaEntry.entries[key]) {
    return null;
  }

  const raw = storage.getItem(entryKey);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn(`[VersionedCache] Failed to parse entry ${key}`, error);
    storage.removeItem(entryKey);
    return null;
  }
}

export function clearCache(schema: string) {
  const manifest = ensureManifest();
  const schemaEntry = manifest.schemas[schema];
  if (!schemaEntry) return;

  clearSchemaEntries(schemaEntry);
  const updatedSchemas = { ...manifest.schemas };
  delete updatedSchemas[schema];
  writeManifest({ updatedAt: new Date().toISOString(), schemas: updatedSchemas });
}
